import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { AiCharacter, AiMatchAnalysis } from "../../types/Ai";
import { getAiCharacters, getMatchAnalyses } from "../../services/aiFeaturesService";
import { useAuth } from "./authContext";

interface AiCommentaryContextProps {
  /** The 6 pundit personas (loaded once). */
  characters: AiCharacter[];
  /** Cached analyses for a match, or undefined if not fetched yet. */
  getAnalyses: (matchId: number) => AiMatchAnalysis[] | undefined;
  /** Lazily fetch + cache a match's analyses (deduped; safe to call every render). */
  ensureAnalyses: (matchId: number) => void;
  isMatchLoading: (matchId: number) => boolean;
}

const AiCommentaryContext = createContext<AiCommentaryContextProps | undefined>(
  undefined
);

// Provides AI pundit commentary to the many fixture tiles. There is no bulk
// endpoint, so each match's analyses are fetched lazily (on first request) and
// cached; the badge uses the cache to decide visibility and the modal reuses the
// same data without a second call.
export const AiCommentaryProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [characters, setCharacters] = useState<AiCharacter[]>([]);
  const [analysesByMatch, setAnalysesByMatch] = useState<
    Map<number, AiMatchAnalysis[]>
  >(new Map());
  const [loadingMatches, setLoadingMatches] = useState<Set<number>>(new Set());

  // Mirrors of cache keys / in-flight ids, read inside the stable ensureAnalyses
  // callback to dedupe without re-creating it (and re-triggering tile effects).
  const loadedRef = useRef<Set<number>>(new Set());
  const inflightRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    // The endpoints require a session; reset cache when signed out so the login
    // page makes no unauthorized request.
    if (!isAuthenticated) {
      setCharacters([]);
      setAnalysesByMatch(new Map());
      setLoadingMatches(new Set());
      loadedRef.current = new Set();
      inflightRef.current = new Set();
      return;
    }
    let cancelled = false;
    getAiCharacters()
      .then((list) => {
        if (!cancelled) setCharacters(list);
      })
      // Non-critical decoration — on failure tiles simply show no badge.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const ensureAnalyses = useCallback(
    (matchId: number) => {
      if (!isAuthenticated) return;
      if (loadedRef.current.has(matchId) || inflightRef.current.has(matchId)) {
        return;
      }
      inflightRef.current.add(matchId);
      setLoadingMatches((prev) => new Set(prev).add(matchId));
      getMatchAnalyses(matchId)
        .then((list) => {
          loadedRef.current.add(matchId);
          setAnalysesByMatch((prev) => new Map(prev).set(matchId, list));
        })
        // Cache an empty result on failure so we don't refetch in a loop.
        .catch(() => {
          loadedRef.current.add(matchId);
          setAnalysesByMatch((prev) => new Map(prev).set(matchId, []));
        })
        .finally(() => {
          inflightRef.current.delete(matchId);
          setLoadingMatches((prev) => {
            const next = new Set(prev);
            next.delete(matchId);
            return next;
          });
        });
    },
    [isAuthenticated]
  );

  const getAnalyses = useCallback(
    (matchId: number) => analysesByMatch.get(matchId),
    [analysesByMatch]
  );

  const isMatchLoading = useCallback(
    (matchId: number) => loadingMatches.has(matchId),
    [loadingMatches]
  );

  const value = useMemo(
    () => ({ characters, getAnalyses, ensureAnalyses, isMatchLoading }),
    [characters, getAnalyses, ensureAnalyses, isMatchLoading]
  );

  return (
    <AiCommentaryContext.Provider value={value}>
      {children}
    </AiCommentaryContext.Provider>
  );
};

function useAiCommentary(): AiCommentaryContextProps {
  const context = useContext(AiCommentaryContext);
  if (!context) {
    throw new Error("useAiCommentary must be used within an AiCommentaryProvider");
  }
  return context;
}

export interface MatchCommentary {
  characters: AiCharacter[];
  analyses: AiMatchAnalysis[];
  loading: boolean;
  /** True once we know the match has at least one generated commentary. */
  hasCommentary: boolean;
}

/** Per-tile hook: triggers the lazy fetch and exposes this match's commentary. */
export function useMatchCommentary(matchId: number): MatchCommentary {
  const { characters, getAnalyses, ensureAnalyses, isMatchLoading } =
    useAiCommentary();

  useEffect(() => {
    ensureAnalyses(matchId);
  }, [matchId, ensureAnalyses]);

  const analyses = getAnalyses(matchId);
  return {
    characters,
    analyses: analyses ?? [],
    loading: isMatchLoading(matchId) || analyses === undefined,
    hasCommentary: (analyses?.length ?? 0) > 0,
  };
}
